/* Rich content per EMR for the dedicated /integrations/:slug pages.
   Keep capability copy *clinic-owner outcome* oriented — not feature dumps.
   Juvonno gets the deepest content because most leads come from Juvonno chains. */

import {
  RefreshCcw, PhoneCall, UserPlus, Database, Pencil, FileText,
  CalendarClock, Globe, ShieldCheck, Webhook,
  Building2, Tag, Brain, ScrollText, ListChecks,
  Headphones, BarChart3, Users,
} from "lucide-react";

/* Generic capabilities shared by Juvonno + Jane App + future EMRs */
const SHARED_CAPABILITIES = [
  {
    Icon: RefreshCcw,
    title: "Real-time 2-way sync",
    desc: "Every booking, edit and cancel reflects in your EMR within ~2 seconds — and the other way around. No overnight CSV jobs.",
  },
  {
    Icon: PhoneCall,
    title: "AI Agent books, cancels & reschedules",
    desc: "The AI Receptionist checks live availability, books straight into the schedule, and handles reschedules + cancellations on the call.",
  },
  {
    Icon: UserPlus,
    title: "Create/Update Patient Profiles",
    desc: "First-time callers, website form submissions and chat leads — the AI creates a new patient profile in the EMR with full intake data, and updates existing profiles on every touch.",
  },
  {
    Icon: Database,
    title: "EMR is the source of truth",
    desc: "CRM and EMR stay locked in sync on patient profiles. We never compete with your chart system — we make it richer.",
  },
  {
    Icon: Pencil,
    title: "Push notes back to the patient profile",
    desc: "Call summaries, intake answers and chat transcripts get pushed straight onto the patient profile — no copy-paste.",
  },
  {
    Icon: ScrollText,
    title: "Get charting updates",
    desc: "Charting events flow back into Clinic OS so reminders, recall and review triggers fire on the right clinical signals.",
  },
  {
    Icon: Globe,
    title: "Website forms → CRM → EMR profile",
    desc: "Every web lead is captured into the CRM, pushed as a booking into the EMR, and stitched into a single patient record automatically.",
  },
  {
    Icon: FileText,
    title: "Intake & consent sync",
    desc: "Send digital intake + consent forms via SMS/email. Once signed, the documents attach to the patient chart on both sides.",
  },
  {
    Icon: Webhook,
    title: "Real-time webhooks",
    desc: "Sub-2-second propagation. The patient hangs up, the appointment is already in the EMR — even before your front desk refreshes.",
  },
  {
    Icon: Tag,
    title: "Tags & segmentation",
    desc: "Tag patients in Clinic OS (post-surgical, lapsed, VIP) and push the tag back to the EMR so reports stay consistent.",
  },
  {
    Icon: ListChecks,
    title: "Reactivation from EMR signals",
    desc: "Dormant patients are auto-surfaced from charting data and queued for win-back outreach — calls, SMS, email.",
  },
  {
    Icon: ShieldCheck,
    title: "Per-record audit log",
    desc: "Every read and write is timestamped, attributed and revocable. HIPAA-ready · SOC 2 aligned · disconnect in one click.",
  },
];

/* Juvonno-exclusive capabilities */
const JUVONNO_EXCLUSIVES = [
  {
    Icon: CalendarClock,
    title: "Smart Practitioner Availability",
    desc: "Reads daily overrides from Juvonno — vacations, blocked time, partial-day off. The AI never offers a slot a clinician quietly killed at 8am.",
    pill: "Juvonno-only",
  },
  {
    Icon: BarChart3,
    title: "Executive Reporting Dashboard",
    desc: "Multi-location performance · clinician utilisation · revenue per location · no-show rate per practitioner · lead-to-booked conversion. Built for chains and group owners.",
    pill: "Juvonno-only",
  },
  {
    Icon: Building2,
    title: "Multi-location aware",
    desc: "Locations, clinicians and appointment types are mapped once. AI routes calls to the right clinic and the right calendar — even if you run 12 locations.",
  },
];

/* Jane App-specific framing — keep it warm + practitioner-loved */
const JANE_EXCLUSIVES = [
  {
    Icon: Headphones,
    title: "AI tuned to wellness-clinic tone",
    desc: "Jane-style warmth. The AI Receptionist speaks like a senior front-desk lead at a multi-disciplinary clinic — not a robot.",
    pill: "Jane App",
  },
  {
    Icon: Brain,
    title: "Reads Jane's appointment types",
    desc: "Knows the difference between an initial assessment, a follow-up and a 60-min massage — and books the right type, duration and clinician.",
  },
  {
    Icon: Users,
    title: "Practitioner profiles respected",
    desc: "Jane's per-practitioner schedule rules — buffers, max bookings, off-days — are honoured by the AI in real time.",
  },
];

/* Outcome stats unique per EMR — small but high-impact */
const JUVONNO_OUTCOMES = [
  { stat: "97%", label: "calls answered", foot: "after 30 days live" },
  { stat: "42%", label: "lift in re-bookings", foot: "from EMR-driven reactivation" },
  { stat: "12+", label: "locations supported", foot: "in our largest live deployment" },
  { stat: "< 14d", label: "to go live", foot: "Juvonno → Clinic OS bridge" },
];

const JANE_OUTCOMES = [
  { stat: "97%", label: "calls answered", foot: "after 30 days live" },
  { stat: "3.4×", label: "more 5-star reviews", foot: "post-visit on autopilot" },
  { stat: "< 10d", label: "to go live", foot: "Jane → Clinic OS bridge" },
];

/* FAQ — quick objection handlers */
const SHARED_FAQ = (emrName) => [
  {
    q: `Will Clinic OS slow down ${emrName} or change how my team uses it?`,
    a: `No. Your team keeps using ${emrName} exactly the way they do today. Clinic OS sits beside it — answering calls, capturing leads, running reactivation — and writes the results into ${emrName} automatically.`,
  },
  {
    q: "Who owns the data?",
    a: `You do. ${emrName} stays the source of truth. We read, write and reconcile — and you can disconnect in one click, with all data intact in your EMR.`,
  },
  {
    q: "How long does the integration take?",
    a: "Most clinics are live in under 14 days. We map locations, clinicians and appointment types, backfill patients, then turn the AI Receptionist on — usually inside the first week.",
  },
  {
    q: "Is it HIPAA & SOC 2 compliant?",
    a: "Yes. TLS 1.3 in transit, AES-256 at rest, per-clinic isolated tenants, full audit log per record write. We can sign a BAA on request.",
  },
];

/* Master EMR-page content registry */
export const EMR_PAGES = {
  juvonno: {
    slug: "juvonno",
    hero: {
      eyebrow: "Built for Juvonno-powered clinics",
      titleBefore: "Clinic OS",
      titleAccent: "+ Juvonno.",
      titleAfter: "One operating system for the whole clinic group.",
      sub: "Juvonno stays your EMR. Clinic OS handles every call, every lead, every re-engagement — and gives executives the multi-location dashboard Juvonno doesn't ship out of the box.",
      brandFromHex: "#0ea5e9",
      brandToHex: "#0d9488",
    },
    capabilities: [...SHARED_CAPABILITIES, ...JUVONNO_EXCLUSIVES],
    outcomes: JUVONNO_OUTCOMES,
    spotlight: {
      kind: "exec-dashboard",
      eyebrow: "Built for Juvonno chains",
      title: "An Executive Reporting Dashboard, finally.",
      body: "Juvonno is incredible for clinicians. But group owners and executives keep asking for one thing — a single dashboard rolling up locations, practitioners and revenue. That's what we built. Live data from Juvonno, refreshed every few minutes, presented for the C-suite.",
      bullets: [
        "Revenue + bookings per location, per practitioner",
        "Clinician utilisation and idle-hour heatmaps",
        "No-show rate per location and clinician",
        "Lead source → booked → first visit → re-booked funnel",
        "Live exec KPI screensaver for HQ TVs",
      ],
    },
    faq: SHARED_FAQ("Juvonno"),
    formTitle: "See your Juvonno clinic on Clinic OS",
    formSub: "Drop your Juvonno URL and we'll walk you through your own data in the demo — including the executive dashboard.",
    formEmrLabel: "Your Juvonno URL",
    formEmrPlaceholder: "yourclinic.juvonno.com",
    showExecReporting: true,
  },

  "jane-app": {
    slug: "jane-app",
    hero: {
      eyebrow: "Built for Jane App-powered clinics",
      titleBefore: "Clinic OS",
      titleAccent: "+ Jane.",
      titleAfter: "Run a calmer clinic without leaving Jane.",
      sub: "Jane stays the schedule, chart and billing system your team already loves. Clinic OS picks up every call, handles website leads and re-engages dormant patients — writing every result back into Jane in real time.",
      brandFromHex: "#10b981",
      brandToHex: "#0d9488",
    },
    capabilities: [...SHARED_CAPABILITIES, ...JANE_EXCLUSIVES],
    outcomes: JANE_OUTCOMES,
    spotlight: {
      kind: "ai-booking",
      eyebrow: "Native to Jane",
      title: "An AI front desk Jane practitioners actually like.",
      body: "Jane's tone is warm, calm and patient-first. Most AI Receptionists sound robotic. Ours is tuned to match the way Jane-loving clinics already speak — so patients can't tell they're being booked by AI.",
      bullets: [
        "Honours Jane's appointment types + durations",
        "Respects per-practitioner buffers and rules",
        "Patient-first script tuned to wellness clinics",
        "Post-visit review automation that compounds",
        "Reactivation pings on Jane recall signals",
      ],
    },
    faq: SHARED_FAQ("Jane"),
    formTitle: "See your Jane clinic on Clinic OS",
    formSub: "Share your Jane URL and we'll build a personalised walk-through using your real schedule and appointment types.",
    formEmrLabel: "Your Jane URL",
    formEmrPlaceholder: "yourclinic.janeapp.com",
    showExecReporting: false,
  },
};

export const findEmrPage = (slug) => EMR_PAGES[slug] || null;
